import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../api";
import { stagePill } from "../ui";
import {
  roleRows, listsOn, noContactOf, wouldReturnTo, howOn, checkLine, historyLine, shortDate, actorWord, personName,
} from "./listsModel.mjs";
import { AddToListMenu, Icon, addToList, moveToNoContact, useConflictAsk } from "./parts";

/*
 * A PERSON (frames 5 and 5b), in the side panel beside a list — never a page
 * change. Their current address and role, every role we know newest first
 * (with why it is joined to them, "Use as current", "Not the same person"),
 * their lists, why the rule list does or does not hold them, and the history.
 *
 * Props:
 *   personId    whose
 *   onClose     the ×
 *   onChange    anything changed (the host reloads its rows)
 *   onToast     (toast) => void, for No Contact's undo
 *   onOpenLead  (contactId) => void, "Open lead record"
 *   allLists    the lists, for "+ Add to list"
 */
export default function PersonPanel({ personId, onClose, onChange, onToast, onOpenLead, allLists = [] }) {
  const [person, setPerson] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const conflictAsk = useConflictAsk("");

  const load = useCallback(async () => {
    try {
      const { person: p } = await api(`/people/${personId}`);
      setPerson(p);
    } catch (e) {
      setErr(e.message);
    }
  }, [personId]);
  useEffect(() => {
    setPerson(null);
    load();
  }, [load]);

  const act = async (fn) => {
    setBusy(true);
    setErr(null);
    try {
      const next = await fn();
      if (next?.person) setPerson(next.person);
      else await load();
      onChange && onChange();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (err && !person) return <div className="lst-person"><div className="lst-psec"><p className="crm-login__err">{err}</p></div></div>;
  if (!person) return <div className="lst-person"><div className="lst-psec lst-sub">Loading…</div></div>;

  const roles = roleRows(person);
  const current = roles.find((r) => r.id === person.role_contact_id) || roles[0];
  const on = listsOn(person);
  const nc = noContactOf(person);
  const back = wouldReturnTo(person);
  const name = personName(person);
  const multi = roles.length > 1;

  const remove = (list) =>
    act(() => api(`/lists/${list.id}/members/remove`, { method: "POST", body: JSON.stringify({ person_ids: [person.person_id] }) }));
  const add = (list) =>
    act(async () => {
      setAdding(false);
      await addToList(list, [person.person_id], conflictAsk.askFor(list.name));
    });
  const toNoContact = () =>
    act(async () => {
      const { toast } = await moveToNoContact([person.person_id]);
      onToast && onToast({ ...toast, title: `${name} moved to No Contact` });
    });
  const offNoContact = () =>
    act(() => api("/lists/no-contact/members/remove", { method: "POST", body: JSON.stringify({ person_ids: [person.person_id] }) }));

  return (
    <div className="lst-person">
      <div className="lst-person__head">
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 className="lst-person__name">{name}</h2>
            {(current?.job_title || current?.company) && (
              <div className="lst-person__role">{[current.job_title, current.company].filter(Boolean).join(" · ")}</div>
            )}
            {person.email && <div className="lst-email" style={{ marginTop: 2 }}>{person.email}</div>}
          </div>
          <button className="crm-btn crm-btn--secondary lst-btn-sm" type="button" aria-label="Close panel" onClick={onClose} style={{ padding: 5 }}>
            <Icon.close />
          </button>
        </div>
        <div className="lst-person__links">
          {onOpenLead && current && <button type="button" className="lst-link" onClick={() => onOpenLead(current.id)}>Open lead record</button>}
          {person.linkedin_url && <a className="lst-link" href={person.linkedin_url} target="_blank" rel="noreferrer">LinkedIn</a>}
          <span className="lst-sub">{roles.length} {roles.length === 1 ? "role" : "roles"} · on {on.length} {on.length === 1 ? "list" : "lists"}</span>
        </div>
      </div>

      {err && <div className="lst-psec"><p className="crm-login__err">{err}</p></div>}

      <div className="lst-psec">
        <h3 className="lst-psec__title">ROLES {multi && <span style={{ fontWeight: 500, letterSpacing: 0 }}>newest first</span>}</h3>
        {roles.map((r) => {
          const pill = stagePill(r.lifecycle_stage);
          const isCur = r.id === person.email_contact_id;
          return (
            <div key={r.id} className={`lst-role${isCur ? " is-cur" : ""}`}>
              <div className="lst-role__l1">
                <span className="lst-role__co">{r.company || "No company"}</span>
                {isCur && <span className="lst-pill lst-pill--k">Current</span>}
                {r.kind === "lead" && r.lifecycle_stage && <span className={`crm-pill ${pill.cls}`}>{pill.label}</span>}
                {r.kind === "associated" && <span className="lst-pill">colleague on a thread</span>}
                {multi && r.join && <span className="lst-join">{r.join}</span>}
                <span style={{ flexGrow: 1 }} />
                <span className="lst-sub">{r.span}</span>
              </div>
              {r.job_title && <div style={{ fontWeight: 700 }}>{r.job_title}</div>}
              <div className="lst-email">{r.email || "no email on this record"}</div>
              {multi && !isCur && (
                <div style={{ display: "flex", gap: 8, marginTop: 4, flexWrap: "wrap" }}>
                  <button className="crm-btn crm-btn--secondary lst-btn-sm" type="button" disabled={busy}
                    onClick={() => act(() => api(`/people/${person.person_id}/current`, { method: "POST", body: JSON.stringify({ contact_id: r.id }) }))}>
                    Use as current
                  </button>
                  <button className="crm-btn crm-btn--secondary lst-btn-sm" type="button" disabled={busy}
                    onClick={() => act(() => api(`/people/${person.person_id}/split`, { method: "POST", body: JSON.stringify({ contact_id: r.id }) }).then(() => load()))}>
                    Not the same person
                  </button>
                </div>
              )}
              {multi && isCur && person.current_pinned && (
                <div style={{ marginTop: 4 }}>
                  <button className="lst-link" type="button" disabled={busy}
                    onClick={() => act(() => api(`/people/${person.person_id}/current`, { method: "POST", body: JSON.stringify({ contact_id: null }) }))}>
                    Chosen by you · go back to the newest
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="lst-psec">
        <h3 className="lst-psec__title">LISTS</h3>
        {nc ? (
          <>
            <div className="lst-nc-box lst-hatch">
              <span className="lst-onwhite" style={{ display: "inline-flex", gap: 6, alignItems: "center" }}><Icon.ban size={13} /><b>No Contact</b></span>
              <span className="lst-onwhite lst-note">since {shortDate(nc.added_at)} {actorWord(nc.changed_by)}</span>
              <span className="lst-onwhite"><button type="button" className="lst-link" style={{ color: "var(--crm-black)" }} onClick={offNoContact} disabled={busy}>Take off No Contact</button></span>
            </div>
            {back.length > 0 && (
              <div className="lst-note" style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                Goes back to: {back.map((l) => <span key={l.id} className="lst-ghost">{l.name}</span>)}
              </div>
            )}
          </>
        ) : (
          <>
            {on.length === 0 && <div className="lst-sub">On no lists.</div>}
            <div>
              {on.map((l) => {
                const how = howOn(l);
                return (
                  <div key={l.id} className="lst-lrow">
                    <div style={{ flex: 1 }}><b>{l.name}</b><div className="lst-sub">{how.word}{how.date ? ` · ${how.date}` : ""}</div></div>
                    <button className="crm-btn crm-btn--secondary lst-btn-sm" type="button" disabled={busy} onClick={() => remove(l)} aria-label={`Remove from ${l.name}`}>Remove</button>
                  </div>
                );
              })}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", position: "relative" }}>
              <button className="crm-btn crm-btn--secondary lst-btn-sm" type="button" disabled={busy} onClick={() => setAdding(true)} aria-expanded={adding}>+ Add to list</button>
              <button className="crm-btn crm-btn--secondary lst-btn-sm" type="button" disabled={busy} onClick={toNoContact}><Icon.ban size={12} />Move to No Contact</button>
              {adding && (
                <AddToListMenu
                  lists={allLists.filter((l) => !on.some((o) => o.id === l.id))}
                  count={1}
                  onPick={add}
                  onClose={() => setAdding(false)}
                  style={{ top: 32, left: 0 }}
                />
              )}
            </div>
          </>
        )}
        {(person.rules || []).map((r) => {
          if (!r.checks) return null;
          const onIt = on.some((l) => l.id === r.listId);
          const failing = r.checks.some((c) => !c.ok);
          return (
            <div key={r.listId} className="lst-muted-box">
              <div style={{ fontSize: 11.5, fontWeight: 700 }}>
                {onIt ? `On ${r.list} ${failing ? "by hand, though the rule says no:" : "because:"}` : `${failing ? "Not on" : "Off"} ${r.list} ${failing ? "because:" : "by hand, though the rule says yes:"}`}
              </div>
              <div className="lst-checks">
                {r.checks.map((c) => <span key={c.key} className={c.ok ? "" : "lst-check--fail"}>{checkLine(c)}</span>)}
              </div>
              {!onIt && !nc && (
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 4 }}>
                  <button className="crm-btn crm-btn--secondary lst-btn-sm" type="button" disabled={busy}
                    onClick={() => add({ id: r.listId, name: r.list })}>
                    {failing ? "Add by hand anyway" : "Put back on"}
                  </button>
                  <span className="lst-sub">A hand add sticks even when the rule says no.</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {person.events?.length > 0 && (
        <div className="lst-psec">
          <h3 className="lst-psec__title">HISTORY</h3>
          <div>
            {person.events.slice(0, 30).map((e) => {
              const h = historyLine(e);
              return (
                <div key={e.id} className="lst-hist">
                  <time>{shortDate(e.occurred_at)}</time>
                  <span>{h.text[0]} <b>{h.text[1]}</b> {h.text[2]}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {conflictAsk.el}
    </div>
  );
}
